import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
	MetaAdsCreateGeoTemplateDto,
	MetaAdsUpdateGeoTemplateDto,
} from "shared";
import { Api } from "../api.config";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export function useGeoSearch(query: string, type: string) {
	const { id: companyId } = useCompany();

	return useQuery({
		queryKey: QueryKeys.Integrations.MetaAds.GeoSearch(companyId, query, type),
		queryFn: async () => {
			const res = await Api.SearchMetaAdsGeoLocations(query, type);
			return res.data;
		},
		enabled: query.length >= 2,
		staleTime: 60_000,
		refetchOnWindowFocus: false,
	});
}

export function useValidateZipCodes() {
	return useMutation({
		mutationFn: async (params: {
			zipCodes: string[];
			countryCode?: string;
		}) => {
			const res = await Api.ValidateMetaAdsZipCodes(
				params.zipCodes,
				params.countryCode,
			);
			return res.data;
		},
	});
}

export function useGeoTemplates() {
	const { id: companyId } = useCompany();

	return useQuery({
		queryKey: QueryKeys.Integrations.MetaAds.GeoTemplates(companyId),
		queryFn: async () => {
			const res = await Api.GetMetaAdsGeoTemplates();
			return res.data;
		},
	});
}

export function useGeoTemplateActions() {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();

	const invalidate = () =>
		queryClient.invalidateQueries({
			queryKey: QueryKeys.Integrations.MetaAds.GeoTemplates(companyId),
		});

	const createTemplate = useMutation({
		mutationFn: async (dto: MetaAdsCreateGeoTemplateDto) => {
			const res = await Api.CreateMetaAdsGeoTemplate(dto);
			return res.data;
		},
		onSuccess: invalidate,
	});

	const updateTemplate = useMutation({
		mutationFn: async (params: {
			id: number;
			dto: MetaAdsUpdateGeoTemplateDto;
		}) => {
			const res = await Api.UpdateMetaAdsGeoTemplate(params.id, params.dto);
			return res.data;
		},
		onSuccess: invalidate,
	});

	const deleteTemplate = useMutation({
		mutationFn: async (id: number) => {
			await Api.DeleteMetaAdsGeoTemplate(id);
		},
		onSuccess: invalidate,
	});

	return { createTemplate, updateTemplate, deleteTemplate };
}
